/**
 * useScrollControl
 *
 * Manages scroll detection and "New message" pill visibility for MathCoachPage.
 *
 * When `enabled` is false (e.g., English sessions), the hook becomes a complete
 * no-op: no listeners are attached, no state is updated, and the returned
 * `pillVisible` is always false. This ensures zero impact on SessionChatPage.
 *
 * AC coverage:
 *   AC-002.1  — scroll position never changed by hook when parent is above fold
 *   AC-002.2  — pillVisible set to true when new message arrives above fold
 *   AC-002.3  — scrollToBottom() hides pill + smooth-scrolls
 *   AC-002.4  — near-bottom on new message → smooth scroll, no pill
 *   AC-002.5  — isInitialLoadRef guard prevents pill on first render
 *   AC-002.6  — enabled=false makes hook a no-op (English tutor unchanged)
 *   AC-002.8  — setPillVisible(true) is idempotent — no flicker
 *   BR-002.1  — never changes scroll position unless near bottom or pill tapped
 *   BR-002.2  — pill only appears in Math Coach (caller passes enabled=true)
 *   BR-002.3  — scroll event auto-dismisses pill when parent scrolls near bottom
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { RefObject } from 'react';
import type { Message } from '@/types/session';

/**
 * Returns true when the element's scroll position is within 100 px of the bottom.
 * A non-scrollable container (scrollHeight === clientHeight) always returns true.
 */
function isNearBottom(el: HTMLDivElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= 100;
}

/**
 * useScrollControl — scroll detection + pill state for MathCoachPage.
 *
 * @param containerRef  Ref to the scrollable chat container div.
 * @param messages      Current message array from useSessionMessages.
 * @param enabled       Feature gate. Pass false for English sessions (no-op).
 *
 * @returns pillVisible  Whether the "New message" pill should be shown.
 * @returns scrollToBottom  Call this to smooth-scroll to the latest message
 *                          and hide the pill.
 */
export function useScrollControl(
  containerRef: RefObject<HTMLDivElement | null>,
  messages: Message[] | undefined,
  enabled: boolean,
): { pillVisible: boolean; scrollToBottom: () => void } {
  const [pillVisible, setPillVisible] = useState(false);

  // AC-002.5: guard that prevents the pill from showing on the very first load.
  const isInitialLoadRef = useRef(true);

  // Tracks how many messages existed on the previous render.
  const prevMessageCountRef = useRef(0);

  // AC-002.3: smooth-scroll to bottom + hide pill.
  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setPillVisible(false);
  }, [containerRef]);

  // Attach a scroll listener to auto-dismiss the pill when the parent manually
  // scrolls back within 100 px of the bottom (BR-002.3 / AC edge case).
  useEffect(() => {
    if (!enabled) return;

    const el = containerRef.current;
    if (!el) return;

    function handleScroll() {
      if (el && isNearBottom(el)) {
        setPillVisible(false);
      }
    }

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, enabled]);

  // Watch for new messages and decide whether to show the pill or auto-scroll.
  useEffect(() => {
    if (!enabled) return;

    const el = containerRef.current;
    if (!el) return;

    const currentCount = messages?.length ?? 0;

    // AC-002.5: on the very first run after mount, stamp the count and return.
    // The initial load guard lets the page settle without triggering a pill.
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      prevMessageCountRef.current = currentCount;
      return;
    }

    // No new messages — nothing to do.
    if (currentCount <= prevMessageCountRef.current) {
      prevMessageCountRef.current = currentCount;
      return;
    }

    prevMessageCountRef.current = currentCount;

    if (isNearBottom(el)) {
      // AC-002.4: parent is at or near the bottom — auto-scroll, no pill.
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      setPillVisible(false);
    } else {
      // AC-002.1 + AC-002.2: parent is reading above — preserve position, show pill.
      // AC-002.8: setPillVisible(true) is idempotent — no flicker on subsequent messages.
      setPillVisible(true);
    }
  }, [messages, enabled, containerRef]);

  return { pillVisible, scrollToBottom };
}
